import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MappingColumns from "../../components/order-import/mapping-column";
import TemplateCard from "../../components/order-import/template-card";
import AddImportCard from "../../components/order-import/add-import-card";
import StepperComponent from "../../components/stepper";
import { toast } from "react-toastify";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import AddIcon from "@mui/icons-material/Add";
import ImportResult from "../../components/order-import/import-result";
import { orderService } from "../../utils/services/orderService";
import { templateService } from "../../utils/services/template-service";
import UpdatedUploadModal from "../../components/file-upload-modal/new-upload-modal";
import ContinuousProgressLoader from "../../components/continuous-progress-loader";
import { useDarkMode } from "../../components/context/darkModeContext";
import { Box } from "@mui/material";

const IMPORT_TYPE = "historical_inventory";

const HistoricalInventoryImport = () => {
  const { darkMode: isDarkMode } = useDarkMode();
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadResponseData, setUploadResponseData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [mappingTemplates, setMappingTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [mappingColumns, setMappingColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedTemplate && selectedTemplate !== "Other") {
      setMappingColumns(
        mappingTemplates.find((x) => x._id === selectedTemplate)
          ?.mapped_column || [],
      );
    } else {
      setMappingColumns([]);
    }
  }, [selectedTemplate]);

  const steps = ["Add/Import", "Select Template", "Mapping", "Verify/Update"];

  const handleStepChange = (newStep) => {
    setActiveStep(newStep);
  };

  useEffect(() => {
    fetchMappingTemplates();
  }, []);

  const fetchMappingTemplates = async () => {
    try {
      const response = await templateService.fetch(IMPORT_TYPE);
      setMappingTemplates(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const uploadFile = async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", data);
      formData.append("type", IMPORT_TYPE);
      const uploadResponse = await orderService.bulkUpload(formData);
      setUploadResponseData(uploadResponse);
      setOpenUploadModal(false);
      setActiveStep(1);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const patchUploadConfig = async () => {
    try {
      await orderService.uploadConfig(
        { start_process: true, mapped_column: mappingColumns },
        uploadResponseData?._id,
      );
      setActiveStep(3);
    } catch (error) {
      console.error(error);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <AddImportCard
            title="Import Your Historical Inventory"
            description="Upload a spreadsheet to bulk-add historical inventory records."
            primaryButtonText="Historical Inventory"
            secondaryButtonText="Upload"
            onClickImport={() => setOpenUploadModal(true)}
            showImages={true}
            images={
              isDarkMode
                ? ["./product1-dark.svg", "./product2-dark.svg"]
                : ["./product1-light.svg", "./product2-light.svg"]
            }
            PrimaryIcon={AddIcon}
            SecondaryIcon={UploadIcon}
            type={IMPORT_TYPE}
            onNext={(data) => uploadFile(data)}
          />
        );
      case 1:
        return (
          <TemplateCard
            title="Select Template"
            description="Start with a template that's just right for you."
            templates={[...mappingTemplates, { _id: "Other", name: "Other" }]}
            onTemplateChange={(value) => setSelectedTemplate(value)}
            onNext={() => {
              if (!selectedTemplate) {
                toast.error("Select the template first!", { autoClose: 2000 });
              } else {
                setActiveStep(2);
              }
            }}
            image={isDarkMode ? "./template-dark.svg" : "./template-light.svg"}
            defaultTemplate="template1"
            displayKey={"name"}
            id={"_id"}
            selectedTemplate={selectedTemplate}
            type={IMPORT_TYPE}
            schema={uploadResponseData?.model_schema}
            fetchTemplates={fetchMappingTemplates}
          />
        );
      case 2:
        return (
          <MappingColumns
            title="Historical Inventory Mapping Columns"
            description="Use the dropdowns to map your spreadsheet columns to historical inventory fields."
            rows={uploadResponseData?.model_schema}
            previewData={uploadResponseData?.preview_record}
            dropdownOptions={uploadResponseData?.file_column}
            onCancel={() => navigate("/historical-inventory?child=true")}
            onProceed={() => patchUploadConfig()}
            onSaveAndProceed={() => patchUploadConfig()}
            mappingColumns={mappingColumns}
            setMappingColumns={setMappingColumns}
            onReturnToTemplateSelection={() => setActiveStep(1)}
          />
        );
      case 3:
        return (
          <ImportResult
            bulkUploadConfig={{
              ...uploadResponseData,
              file_column: (uploadResponseData?.file_column || []).filter(
                (x) => x,
              ),
            }}
            onFinish={() => navigate("/historical-inventory?child=true")}
            type={IMPORT_TYPE}
          />
        );
      default:
        return <Typography variant="h6">Unknown Step</Typography>;
    }
  };

  return (
    <div>
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <ContinuousProgressLoader
            variant="wave"
            size="large"
            color="primary"
            message="Processing records in background..."
          />
        </Box>
      )}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
        <StepperComponent
          activeStep={activeStep}
          steps={steps}
          handleStepClick={handleStepChange}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
        {renderStepContent()}
      </div>
      <UpdatedUploadModal
        onClickShowUpload={() => navigate("/upload-status")}
        onSubmitUploadData={(e) => uploadFile(e)}
        onClose={() => setOpenUploadModal(false)}
        open={openUploadModal}
        title={"Historical Inventory"}
        type={IMPORT_TYPE}
      />
    </div>
  );
};

export default HistoricalInventoryImport;
